import type { TFunction } from 'i18next';
import { type Dayjs } from 'dayjs';
import { formatDurationLabel, getDateRangeFromPreset } from '@/utils/dateFormat';

export const CONVERSATION_OVERVIEW_PERIODS = [
  { value: 'today', labelKey: 'conversationOverview.periods.today' },
  { value: '7d', labelKey: 'conversationOverview.periods.7d' },
  { value: '30d', labelKey: 'conversationOverview.periods.30d' },
  { value: 'custom', labelKey: 'conversationOverview.periods.custom' }
] as const;

export type ConversationOverviewPeriod = (typeof CONVERSATION_OVERVIEW_PERIODS)[number]['value'];

export function getConversationOverviewPeriods(t: TFunction) {
  return CONVERSATION_OVERVIEW_PERIODS.map((period) => ({
    value: period.value,
    label: t(period.labelKey)
  }));
}

export function getDateRangeFromPeriod(
  period: ConversationOverviewPeriod,
  customRange: [Dayjs | null, Dayjs | null] | null
): [Dayjs, Dayjs] | null {
  if (period !== 'custom') return getDateRangeFromPreset(period, true);
  if (customRange?.[0] && customRange?.[1]) {
    return [customRange[0].startOf('day'), customRange[1].endOf('day')];
  }
  return null;
}

export function formatDurationDisplay(
  channel: 'WEBCHAT' | 'VOICE' | 'EMAIL',
  durationMs: number,
  t: TFunction
): { text: string; indicator?: 'veryShort' | 'veryLong' } {
  const seconds = Math.max(0, Math.floor(durationMs / 1000));
  const plain = formatDurationLabel(durationMs, t, 'conversationOverview.duration', 'minute');

  if (channel === 'VOICE') {
    if (seconds < 10) return { text: plain, indicator: 'veryShort' };
    if (seconds > 10 * 60) return { text: plain, indicator: 'veryLong' };
    return { text: plain };
  }

  if (channel === 'EMAIL') {
    return { text: formatDurationLabel(durationMs, t, 'conversationOverview.duration', 'hour') };
  }

  if (seconds < 30) return { text: plain, indicator: 'veryShort' };
  if (seconds > 15 * 60) return { text: plain, indicator: 'veryLong' };
  return { text: plain };
}
